/*
  Layout-Hülle für /shiftplan_v2.
*/

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ContentPasteRoundedIcon from "@mui/icons-material/ContentPasteRounded";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import useToast from "../../../components/useToast";
import AppLoadingOverlay from "../../../design-system/components/AppLoadingOverlay";
import { IconAdd, IconChevronRight, IconDelete, IconEdit, IconHoliday } from "../../../design-system/icons";
import { normalizeApiError } from "../api/errors";
import {
  buildQuickTimeOffDrafts,
  buildQuickTimeOffRange,
  buildCreateEventFromDemandDrop,
  buildCreateEventsFromCopiedEntry,
  buildDeleteEventFromEntry,
  buildMoveEventFromShift,
  buildOptimisticCopiedEntries,
  buildOptimisticMovedShift,
  buildOptimisticResizedEntry,
  buildReleaseEventsFromShifts,
  buildResizeEventFromEntry,
  buildSaveEventFromEditorDraft,
  isReleasableDraftShift,
} from "../domain/model/schedulerEvents";
import {
  getCellKey,
  mergeCellSelections,
  normalizeCellSelection,
  toggleCellContext,
} from "../domain/model/cellSelection";
import { isDemandDragEntry } from "../domain/model/demand";
import { SHIFT_DISPLAY_MODES } from "../domain/model/shiftDisplay";
import { getShiftDayKeys, getShiftDepartmentRowId } from "../domain/model/selectors";
import { getUserHolidayDayKeys } from "../domain/model/userHolidays";
import ShiftEditorPopup from "../features/editor/ShiftEditorPopup";
import ShiftplanExportDialog from "../features/export/ShiftplanExportDialog";
import { EXPORT_FORMATS, normalizeExportFormat } from "../features/export/exportFormats";
import ShiftplanMonthGrid from "../features/grid/ShiftplanMonthGrid";
import ConflictsPanel from "../features/panels/ConflictsPanel";
import EmployeeStatsPanel from "../features/panels/EmployeeStatsPanel";
import PeriodCopyPanel from "../features/panels/PeriodCopyPanel";
import SolverPanel from "../features/panels/SolverPanel";
import useSolverRuns from "../features/panels/useSolverRuns";
import SettingsPanel from "../features/panels/SettingsPanel";
import ShiftplanV2Toolbar from "../features/toolbar/ShiftplanV2Toolbar";
import PeriodSummaryBar from "../features/ui/PeriodSummaryBar";

function getDragStartCellContext(cell, days, anchorDayOffset = 0) {
  const safeOffset = Math.max(0, Math.floor(Number(anchorDayOffset) || 0));
  if (!cell || !safeOffset) return cell;

  const targetIndex = (Array.isArray(days) ? days : []).findIndex((day) => day.key === cell?.day?.key);
  if (targetIndex < 0) return cell;

  const startDay = days[Math.max(0, targetIndex - safeOffset)];
  return startDay ? { ...cell, day: startDay } : cell;
}

function parseDateKey(value) {
  const match = String(value || "").slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

function formatDateKey(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDaysToDateKey(dateKey, dayOffset) {
  const date = parseDateKey(dateKey);
  if (!date) return "";

  date.setDate(date.getDate() + Math.max(0, Number(dayOffset) || 0));
  return formatDateKey(date);
}

function getCalendarEntryDayCount(entry) {
  if (!entry) return 1;
  if (isUserHolidayEntry(entry)) return Math.max(1, getUserHolidayDayKeys(entry).length);
  if (entry.entryType === "absence") return Math.max(1, getShiftDayKeys(entry).length);
  return 1;
}

function buildMovedCalendarEntryRange(entry, targetCell) {
  const startDate = String(targetCell?.day?.key || "").slice(0, 10);
  if (!entry || !startDate) return null;

  return {
    startDate,
    endDate: addDaysToDateKey(startDate, getCalendarEntryDayCount(entry) - 1),
  };
}

function isCalendarEntryAlreadyAtTarget(entry, targetCell, range) {
  if (!entry || !targetCell || !range) return false;

  const targetUserId = String(targetCell?.row?.id || "");
  if (!targetUserId) return false;

  if (isUserHolidayEntry(entry)) {
    return String(entry.userId || "") === targetUserId && String(entry.startDate || "") === range.startDate;
  }

  if (entry.entryType === "absence") {
    return String(entry.employeeId || "") === targetUserId && String(entry.dateKey || "").slice(0, 10) === range.startDate;
  }

  return false;
}

const EMPTY_DRAG_STATE = { entry: null, targetCellKey: "", anchorDayOffset: 0 };
const DEMAND_EXPANDED_STORAGE_KEY = "spv2_demand_row_expanded";
const DEMAND_VISIBLE_STORAGE_KEY = "spv2_demand_row_visible";
const SHIFTPLAN_TOAST_OPTIONS = {};

/*
  Lokal für den vollständigen Solver-Test freigeschaltet. Vor einem Rollout
  müssen Berechtigungen, Worker und Tenant-Migrationen separat bestätigt sein.
*/
const SOLVER_PANEL_ENABLED = true;
const SOLVER_PREVIEW_MESSAGE =
  "Automatisch planen ist noch in Arbeit – wir schalten es frei, sobald es so weit ist.";

function readStoredFlag(storageKey, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === null) return fallback;
    return raw === "1";
  } catch (_error) {
    return fallback;
  }
}

function persistStoredFlag(storageKey, value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, value ? "1" : "0");
  } catch (_error) {
    // Persistenz ist Komfort; ohne localStorage bleibt der Zustand pro Sitzung.
  }
}

function formatDemandDayLabel(dateKey) {
  const date = parseDateKey(dateKey);
  if (!date) return String(dateKey || "");
  return new Intl.DateTimeFormat("de-CH", { weekday: "short", day: "2-digit", month: "2-digit" }).format(date);
}

function isShiftAlreadyInCell(shift, cell) {
  if (!shift || !cell) return false;
  const sameDate = String(shift.dateKey || "") === String(cell?.day?.key || "");
  if (!sameDate) return false;

  if (cell.rowMode === "employee") {
    return String(shift.employeeId || "") === String(cell?.row?.id || "");
  }

  if (cell.rowMode === "department") {
    return String(shift.department || "") === String(cell?.row?.name || cell?.row?.id || "");
  }

  return false;
}

function isTypingTarget(target) {
  const tagName = String(target?.tagName || "").toLowerCase();
  return tagName === "input" || tagName === "textarea" || tagName === "select" || Boolean(target?.isContentEditable);
}

function isMultiSelectEvent(event) {
  return Boolean(event?.shiftKey || event?.ctrlKey || event?.metaKey);
}

function getEntryKey(entry) {
  return String(entry?.id || "").trim();
}

function normalizeEntryGroup(entryOrEntries) {
  const entries = Array.isArray(entryOrEntries) ? entryOrEntries : [entryOrEntries];
  const seenKeys = new Set();

  return entries.filter((entry) => {
    const key = getEntryKey(entry);
    if (!entry || !key || seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });
}

function toggleEntryInGroup(currentEntries, entry) {
  const entryKey = getEntryKey(entry);
  if (!entryKey) return normalizeEntryGroup(currentEntries);

  const entries = normalizeEntryGroup(currentEntries);
  const exists = entries.some((item) => getEntryKey(item) === entryKey);
  return exists ? entries.filter((item) => getEntryKey(item) !== entryKey) : [...entries, entry];
}

function getContextMenuPosition(event, itemCount = 3, width = 192) {
  const height = itemCount * 36 + 14;
  const viewportWidth = Math.max(320, Number(window.innerWidth || 0));
  const viewportHeight = Math.max(240, Number(window.innerHeight || 0));

  return {
    x: Math.max(8, Math.min(Number(event.clientX || 0), viewportWidth - width - 8)),
    y: Math.max(8, Math.min(Number(event.clientY || 0), viewportHeight - height - 8)),
  };
}

/*
  Lage des Untermenüs.

  Waagrecht rechnet bewusst das CSS: das Menü hat nur eine min-width und wächst
  mit dem Text, seine echte Breite kennt hier niemand. Früher stand hier 192px
  als Festwert – war das Menü schmaler, klaffte genau diese Differenz als Lücke
  zwischen Menü und Untermenü, und die Hover-Brücke lag daneben statt darin.

  Hier bleiben darum nur die Richtung (nach links, wenn rechts kein Platz ist),
  die Höhe und der senkrechte Versatz.
*/
function getContextSubmenuLayout(menu, itemCount) {
  const viewportWidth = Math.max(320, Number(window.innerWidth || 0));
  const viewportHeight = Math.max(240, Number(window.innerHeight || 0));
  const menuX = Number(menu?.x || 0);
  const menuY = Number(menu?.y || 0);
  // Für die Richtungsentscheidung genügt eine Schätzung der Menübreite.
  const estimatedMenuWidth = 192;
  const submenuWidth = 224;
  const triggerOffsetTop = 78;
  const triggerHeight = 34;
  const maxHeight = Math.max(96, Math.min(320, viewportHeight - 24, itemCount * 36 + 14));
  const flipped = menuX + estimatedMenuWidth + submenuWidth + 8 > viewportWidth;
  let top = -6;

  const bottomOverflow = menuY + triggerOffsetTop + top + maxHeight - (viewportHeight - 8);
  if (bottomOverflow > 0) top -= bottomOverflow;

  const topOverflow = 8 - (menuY + triggerOffsetTop + top);
  if (topOverflow > 0) top += topOverflow;

  /*
    Die Brücke deckt die ganze Höhe des Untermenüs ab, nicht nur die des
    Eintrags: sonst verliert ein schräger Weg zu einem unteren Punkt den Hover,
    noch bevor er das Untermenü erreicht.
  */
  const bridgeTop = Math.min(top, 0) - 4;
  const bridgeHeight = Math.max(top + maxHeight, triggerHeight) + 4 - bridgeTop;

  return {
    flipped,
    style: {
      "--spv2-context-submenu-top": `${Math.round(top)}px`,
      "--spv2-context-submenu-width": `${submenuWidth}px`,
      "--spv2-context-submenu-max-height": `${Math.round(maxHeight)}px`,
      "--spv2-context-submenu-bridge-top": `${Math.round(bridgeTop)}px`,
      "--spv2-context-submenu-bridge-height": `${Math.round(bridgeHeight)}px`,
    },
  };
}

const CONTEXT_TIME_OFF_SOURCES = Object.freeze({
  ABSENCE: "absence",
  USER_HOLIDAY: "user_holiday",
});

function normalizeContextTimeOffOptions(absenceOptions, userHolidayTypes) {
  const absences = (Array.isArray(absenceOptions) ? absenceOptions : [])
    .map((option) => {
      const id = String(option?.id ?? option?.AbsenceID ?? option?.absenceId ?? "").trim();
      const name = String(option?.name ?? option?.label ?? option?.title ?? "").trim();
      if (!name) return null;

      return {
        key: `${CONTEXT_TIME_OFF_SOURCES.ABSENCE}:${id || name}`,
        id: id || name,
        name,
        source: CONTEXT_TIME_OFF_SOURCES.ABSENCE,
        option: {
          ...option,
          id: id || option?.id,
          key: `${CONTEXT_TIME_OFF_SOURCES.ABSENCE}:${id || name}`,
          name,
          source: CONTEXT_TIME_OFF_SOURCES.ABSENCE,
        },
      };
    })
    .filter(Boolean);

  const holidays = (Array.isArray(userHolidayTypes) ? userHolidayTypes : [])
    .map((option) => {
      const id = String(option?.id ?? option?.UserHolidayTypeID ?? option?.userHolidayTypeId ?? "").trim();
      const name = String(option?.name ?? option?.label ?? option?.title ?? "").trim();
      if (!id || !name) return null;

      return {
        key: `${CONTEXT_TIME_OFF_SOURCES.USER_HOLIDAY}:${id}`,
        id,
        name,
        source: CONTEXT_TIME_OFF_SOURCES.USER_HOLIDAY,
        option: {
          ...option,
          id,
          key: `${CONTEXT_TIME_OFF_SOURCES.USER_HOLIDAY}:${id}`,
          name,
          source: CONTEXT_TIME_OFF_SOURCES.USER_HOLIDAY,
        },
      };
    })
    .filter(Boolean);

  return [...absences, ...holidays];
}

function sortShiftsByStart(left, right) {
  const startCompare = String(left?.start || "").localeCompare(String(right?.start || ""));
  if (startCompare !== 0) return startCompare;
  return String(left?.title || "").localeCompare(String(right?.title || ""), "de", { sensitivity: "base" });
}

function getShiftCellRowId(shift, rowMode) {
  return rowMode === "department" ? getShiftDepartmentRowId(shift) : String(shift?.employeeId || "");
}

function addShiftsToCells(shiftsByCell, shifts, rowMode) {
  return (Array.isArray(shifts) ? shifts : []).reduce(
    (accumulator, shift) => {
      if (!shift?.id) return accumulator;

      const rowId = getShiftCellRowId(shift, rowMode);
      if (!rowId) return accumulator;

      getShiftDayKeys(shift).forEach((dateKey) => {
        const cellKey = `${rowId}::${dateKey}`;
        accumulator[cellKey] = [...(accumulator[cellKey] || []), shift].sort(sortShiftsByStart);
      });

      return accumulator;
    },
    { ...(shiftsByCell || {}) }
  );
}

function applyOptimisticMovedShift(shiftsByCell, optimisticMove, rowMode) {
  const movedShift = optimisticMove?.shift;
  if (!movedShift?.id) return shiftsByCell;

  const nextShiftsByCell = Object.entries(shiftsByCell || {}).reduce((accumulator, [cellKey, cellShifts]) => {
    const nextCellShifts = (Array.isArray(cellShifts) ? cellShifts : []).filter(
      (shift) => String(shift?.id) !== String(movedShift.id)
    );
    if (nextCellShifts.length) accumulator[cellKey] = nextCellShifts;
    return accumulator;
  }, {});

  return addShiftsToCells(nextShiftsByCell, [movedShift], rowMode);
}

function applyOptimisticPastedEntries(shiftsByCell, optimisticPaste, rowMode) {
  return addShiftsToCells(shiftsByCell, optimisticPaste?.shifts || [], rowMode);
}

function removeEntryFromCells(entriesByCell, entryId) {
  const entryKey = getEntryKey({ id: entryId });
  if (!entryKey) return entriesByCell;

  return Object.entries(entriesByCell || {}).reduce((accumulator, [cellKey, entries]) => {
    const nextEntries = (Array.isArray(entries) ? entries : []).filter(
      (entry) => String(entry?.id || "") !== entryKey
    );
    if (nextEntries.length) accumulator[cellKey] = nextEntries;
    return accumulator;
  }, {});
}

function addUserHolidaysToCells(userHolidaysByCell, userHolidays) {
  return (Array.isArray(userHolidays) ? userHolidays : []).reduce(
    (accumulator, holiday) => {
      const userId = String(holiday?.userId || "");
      if (!userId) return accumulator;

      getUserHolidayDayKeys(holiday).forEach((dateKey) => {
        const cellKey = `${userId}::${dateKey}`;
        accumulator[cellKey] = [...(accumulator[cellKey] || []), holiday];
      });

      return accumulator;
    },
    { ...(userHolidaysByCell || {}) }
  );
}

function applyOptimisticPastedUserHolidays(userHolidaysByCell, optimisticPaste) {
  return addUserHolidaysToCells(userHolidaysByCell, optimisticPaste?.userHolidays || []);
}

function applyOptimisticResizedShift(shiftsByCell, optimisticResize, rowMode) {
  const resizedEntry = optimisticResize?.entry;
  if (!resizedEntry?.id || resizedEntry.entryType !== "absence") return shiftsByCell;

  const withoutPreviousEntry = removeEntryFromCells(shiftsByCell, resizedEntry.id);
  return addShiftsToCells(withoutPreviousEntry, [resizedEntry], rowMode);
}

function applyOptimisticResizedUserHoliday(userHolidaysByCell, optimisticResize) {
  const resizedEntry = optimisticResize?.entry;
  if (!isUserHolidayEntry(resizedEntry)) return userHolidaysByCell;

  const withoutPreviousEntry = removeEntryFromCells(userHolidaysByCell, resizedEntry.id);
  return addUserHolidaysToCells(withoutPreviousEntry, [resizedEntry]);
}

function releaseDepartmentName(shift) {
  return String(shift?.department || "Ohne Abteilung").trim() || "Ohne Abteilung";
}

function releaseDepartmentKey(value) {
  return String(value || "Ohne Abteilung").trim().toLocaleLowerCase("de") || "ohne abteilung";
}

function isUserHolidayEntry(entry) {
  return Boolean(entry?.holidayTypeId || entry?.userHolidayTypeId || entry?.entryType === "user_holiday");
}

function getCopiedEntryLabel(entry) {
  if (isUserHolidayEntry(entry)) return "Ferien";
  if (entry?.entryType === "absence") return "Absenz";
  return "Schicht";
}

function getEntryGroupLabel(entries) {
  const normalizedEntries = normalizeEntryGroup(entries);
  if (normalizedEntries.length === 1) return getCopiedEntryLabel(normalizedEntries[0]);
  return `${normalizedEntries.length} Einträge`;
}

function buildReleaseDepartmentOptions(shifts) {
  const departments = new Map();

  (Array.isArray(shifts) ? shifts : []).forEach((shift) => {
    const name = releaseDepartmentName(shift);
    const key = releaseDepartmentKey(name);
    const current = departments.get(key) || { key, name, count: 0 };
    departments.set(key, { ...current, count: current.count + 1 });
  });

  return Array.from(departments.values()).sort((left, right) =>
    left.name.localeCompare(right.name, "de", { sensitivity: "base" })
  );
}

function filterReleaseShiftsByDepartment(targetShifts, departmentKey) {
  if (!departmentKey) return Array.isArray(targetShifts) ? targetShifts : [];
  return (Array.isArray(targetShifts) ? targetShifts : []).filter(
    (shift) => releaseDepartmentKey(releaseDepartmentName(shift)) === departmentKey
  );
}

function normalizeDateTimeText(value) {
  return String(value || "").slice(0, 19);
}

function isPersistentApiId(value) {
  return /^\d+$/.test(String(value || ""));
}

function findSavedShiftForEvent(savedData, event) {
  const shifts = Array.isArray(savedData?.shifts) ? savedData.shifts : [];
  if (!event || !shifts.length) return null;

  if (isPersistentApiId(event.id)) {
    const byId = shifts.find((shift) => String(shift.id) === String(event.id));
    if (byId) return byId;
  }

  const entryType = event.entry_type === "custom_shift" ? "custom_shift" : "shift";
  const startAt = normalizeDateTimeText(event.start_at);
  const endAt = normalizeDateTimeText(event.end_at);
  const userId = String(event.user_id || "");
  const shiftId = String(event.shift_id || "");
  const customName = String(event.custom_shift_name || "").trim().toLocaleLowerCase("de");
  const departmentName = String(event.department_name || "").trim().toLocaleLowerCase("de");

  return [...shifts].reverse().find((shift) => {
    if (shift.entryType !== entryType || !shift.isDraft) return false;
    if (String(shift.employeeId || "") !== userId) return false;
    if (normalizeDateTimeText(shift.start) !== startAt || normalizeDateTimeText(shift.end) !== endAt) return false;
    if (entryType === "shift" && String(shift.shiftId || "") !== shiftId) return false;
    if (entryType === "custom_shift" && customName && String(shift.title || "").trim().toLocaleLowerCase("de") !== customName) {
      return false;
    }
    if (!customName && departmentName && String(shift.department || "").trim().toLocaleLowerCase("de") !== departmentName) {
      return false;
    }
    return true;
  });
}

function ContextMenuItem({ icon, label, suffix = null, danger = false, disabled = false, onClick }) {
  return (
    <button
      type="button"
      role="menuitem"
      className={`${danger ? "is-danger" : ""}${suffix ? " has-suffix" : ""}`}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="spv2-grid-context-menu-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="spv2-grid-context-menu-label">{label}</span>
      {suffix ? <span className="spv2-grid-context-menu-suffix">{suffix}</span> : null}
    </button>
  );
}

function ContextMenuSubmenu({ icon, label, disabled = false, layout, onClick, children }) {
  const hasChildren = React.Children.count(children) > 0;

  return (
    <div
      className={`spv2-grid-context-menu-submenu${layout?.flipped ? " is-flipped" : ""}${
        disabled ? " is-disabled" : ""
      }`}
      style={layout?.style}
    >
      <ContextMenuItem
        icon={icon}
        label={label}
        suffix={hasChildren ? <IconChevronRight fontSize="small" /> : null}
        disabled={disabled}
        onClick={onClick}
      />
      {hasChildren ? (
        <div className="spv2-grid-context-submenu" role="menu" aria-label={label}>
          {children}
        </div>
      ) : null}
    </div>
  );
}

function GridContextMenu({
  menu,
  absenceOptions = [],
  userHolidayTypes = [],
  hasCopiedEntry,
  isSaving,
  onCopy,
  onPaste,
  onEdit,
  onEditUserHoliday,
  onCreate,
  onCreateAbsence,
  onQuickCreateTimeOff,
  onDelete,
}) {
  if (!menu) return null;

  const isShiftMenu = menu.type === "shift";
  const isUserHolidayMenu = menu.type === "user_holiday";
  const menuEntries = normalizeEntryGroup(isUserHolidayMenu ? menu.holiday : menu.entries || menu.shift);
  const contextTimeOffOptions = normalizeContextTimeOffOptions(absenceOptions, userHolidayTypes);
  const cellContexts = menu.contexts || [menu.context];
  const menuEntryLabel = menuEntries.length > 1 ? `${menuEntries.length} Einträge` : "";

  return (
    <Box
      className="spv2-grid-context-menu"
      style={{ left: `${menu.x}px`, top: `${menu.y}px` }}
      role="menu"
      aria-label="Schichtplan Aktion"
      onClick={(event) => event.stopPropagation()}
      onContextMenu={(event) => event.preventDefault()}
    >
      {isShiftMenu || isUserHolidayMenu ? (
        <>
          <ContextMenuItem
            icon={<ContentCopyRoundedIcon fontSize="small" />}
            label={menuEntryLabel ? `${menuEntryLabel} kopieren` : "Kopieren"}
            disabled={isSaving}
            onClick={() => onCopy(isUserHolidayMenu ? menu.holiday : menuEntries)}
          />
          <ContextMenuItem
            icon={<IconEdit fontSize="small" />}
            label="Bearbeiten"
            disabled={isSaving}
            onClick={() => (isUserHolidayMenu ? onEditUserHoliday?.(menu.context) : onEdit(menu.context))}
          />
          {isShiftMenu || isUserHolidayMenu ? (
            <ContextMenuItem
              icon={<IconDelete fontSize="small" />}
              label={menuEntryLabel ? `${menuEntryLabel} löschen` : "Löschen"}
              danger
              disabled={isSaving}
              onClick={() => onDelete(isUserHolidayMenu ? menu.holiday : menuEntries)}
            />
          ) : null}
        </>
      ) : (
        <>
          <ContextMenuItem
            icon={<ContentPasteRoundedIcon fontSize="small" />}
            disabled={isSaving || !hasCopiedEntry}
            label={
              Array.isArray(menu.contexts) && menu.contexts.length > 1
                ? `In ${menu.contexts.length} Zellen einfügen`
                : "Einfügen"
            }
            onClick={() => onPaste(menu.contexts || [menu.context])}
          />
          <ContextMenuItem
            icon={<IconAdd fontSize="small" />}
            label="Schicht erstellen"
            disabled={isSaving}
            onClick={() => onCreate(menu.context)}
          />
          <ContextMenuSubmenu
            icon={<IconAdd fontSize="small" />}
            label="Absenz erfassen"
            disabled={isSaving}
            layout={getContextSubmenuLayout(menu, contextTimeOffOptions.length)}
            onClick={() => onCreateAbsence?.(menu.context, null, cellContexts)}
          >
            {contextTimeOffOptions.map((timeOffOption) => (
              <ContextMenuItem
                key={timeOffOption.key}
                icon={
                  timeOffOption.source === CONTEXT_TIME_OFF_SOURCES.USER_HOLIDAY ? (
                    <IconHoliday fontSize="small" />
                  ) : (
                    <EventBusyOutlinedIcon fontSize="small" />
                  )
                }
                label={timeOffOption.name}
                disabled={isSaving}
                onClick={() => onQuickCreateTimeOff?.(menu.context, timeOffOption.option, cellContexts)}
              />
            ))}
          </ContextMenuSubmenu>
        </>
      )}
    </Box>
  );
}

export default function ShiftplanV2Layout({
  viewMode,
  rowMode,
  referenceDate,
  periodLabel,
  searchQuery,
  isLoading,
  error,
  days,
  rows,
  employeeOrderRows,
  departmentOrderRows,
  departments,
  shiftTemplates,
  absenceOptions,
  userHolidayTypes,
  userHolidays,
  publicHolidayNamesByDate,
  dayCommentsByDate = {},
  onSaveDayComment,
  shifts,
  allShifts,
  draftShifts,
  onLoadPlanRange,
  shiftsByCell,
  availabilityEntries = [],
  availabilityDefaults = [],
  availabilityByCell,
  companyAvailabilityMode = "absence",
  userAvailabilityModes = {},
  userHolidaysByCell,
  availabilityAbsencesByCell,
  workloadByEmployeeId,
  demandModel = { byDay: {}, hasDemand: false, totalRequired: 0, totalMissing: 0 },
  conflictModel = { conflicts: [], totalCount: 0, countsByType: {} },
  periodSummary,
  workingDaysPerWeek,
  vacationAbsenceIds,
  displayCalendar,
  locationName = "",
  // Für den Kopf des PDF-Aushangs ("SCHICHTPLAN SportRock").
  companyName = "",
  shiftDisplayMode = SHIFT_DISPLAY_MODES.TIME_DEPARTMENT,
  onViewModeChange,
  onRowModeChange,
  onSearchQueryChange,
  onPreviousPeriod,
  onNextPeriod,
  onPeriodSelect,
  onSaveEmployeeOrder,
  onResetEmployeeOrder,
  onSaveDepartmentOrder,
  onResetDepartmentOrder,
  onDisplayCalendarChange,
  onShiftDisplayModeChange,
  onDepartmentColorChange,
  onShiftColorChange,
  onPublicHolidaysChanged,
  onSaveSchedulerEvents,
  onSendReleaseNotifications,
  onSolverTransferred,
  accessLevel = "",
  // Zusatzmodul WhatsApp: ohne das Modul entfaellt der Benachrichtigungsschritt
  // in der Freigabe.
  whatsappSubscription = false,
  wiesnshopExcelExport = false,
}) {
  const { t } = useTranslation();
  const {
    success: showSuccessToast,
    info: showInfoToast,
    warning: showWarningToast,
    error: showErrorToast,
    loading: showLoadingToast,
    close: closeToast,
  } = useToast(SHIFTPLAN_TOAST_OPTIONS);
  const [settingsPanel, setSettingsPanel] = useState({ open: false, section: "order" });
  const [employeeStatsPanel, setEmployeeStatsPanel] = useState({ open: false, employeeId: "" });
  const [isPeriodCopyOpen, setIsPeriodCopyOpen] = useState(false);
  const [isSolverOpen, setIsSolverOpen] = useState(false);
  const [exportDialog, setExportDialog] = useState({ open: false, format: EXPORT_FORMATS.PDF });
  const [editorContext, setEditorContext] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedShiftGroup, setSelectedShiftGroup] = useState([]);
  const [selectedUserHoliday, setSelectedUserHoliday] = useState(null);
  const [selectedCells, setSelectedCells] = useState([]);
  const [copiedEntry, setCopiedEntry] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [dragState, setDragState] = useState(EMPTY_DRAG_STATE);
  const [optimisticMove, setOptimisticMove] = useState(null);
  const [optimisticPaste, setOptimisticPaste] = useState(null);
  const [optimisticResize, setOptimisticResize] = useState(null);
  const [isSavingAction, setIsSavingAction] = useState(false);
  const [showAvailability, setShowAvailability] = useState(false);
  const [demandExpanded, setDemandExpanded] = useState(() => readStoredFlag(DEMAND_EXPANDED_STORAGE_KEY, false));
  const [showDemand, setShowDemand] = useState(() => readStoredFlag(DEMAND_VISIBLE_STORAGE_KEY, true));
  const [releasePreview, setReleasePreview] = useState(null);
  const [conflictPreview, setConflictPreview] = useState(null);
  const [isConflictsPanelOpen, setIsConflictsPanelOpen] = useState(false);
  const feedbackToastKeyRef = useRef(null);
  const selectedCellKeys = useMemo(() => new Set(selectedCells.map(getCellKey).filter(Boolean)), [selectedCells]);
  const selectedShiftIds = useMemo(
    () => new Set(selectedShiftGroup.map((shift) => getEntryKey(shift)).filter(Boolean)),
    [selectedShiftGroup]
  );
  const renderedShiftsByCell = useMemo(() => {
    const withMove = applyOptimisticMovedShift(shiftsByCell, optimisticMove, rowMode);
    const withResize = applyOptimisticResizedShift(withMove, optimisticResize, rowMode);
    return applyOptimisticPastedEntries(withResize, optimisticPaste, rowMode);
  }, [optimisticMove, optimisticPaste, optimisticResize, rowMode, shiftsByCell]);
  const renderedUserHolidaysByCell = useMemo(
    () =>
      applyOptimisticPastedUserHolidays(
        applyOptimisticResizedUserHoliday(userHolidaysByCell, optimisticResize),
        optimisticPaste
      ),
    [optimisticPaste, optimisticResize, userHolidaysByCell]
  );
  /*
    Freigabe-Grundmenge: die offenen Entwürfe des ganzen Plans, nicht nur der
    angezeigten Periode. Sie kommen als eigene Anfrage (draftShifts), seit der
    Plan nur noch den Sichtbereich lädt — "Alle freigeben" muss auch Entwürfe
    erwischen, die weiter in der Zukunft liegen.

    Bis diese Anfrage zurück ist, zählt der angezeigte Zeitraum. Das ist zu
    wenig statt zu viel: Der Knopf verspricht nie eine Freigabe, die er nicht
    ausführt.
  */
  const releasableDraftShifts = useMemo(
    () => (Array.isArray(draftShifts) && draftShifts.length ? draftShifts : allShifts || shifts || [])
      .filter(isReleasableDraftShift)
      .sort(sortShiftsByStart),
    [allShifts, draftShifts, shifts]
  );
  const periodDraftShifts = useMemo(() => {
    const dayKeys = new Set((Array.isArray(days) ? days : []).map((day) => String(day?.key || "")).filter(Boolean));
    if (!dayKeys.size) return [];

    return releasableDraftShifts.filter((shift) => getShiftDayKeys(shift).some((dateKey) => dayKeys.has(dateKey)));
  }, [days, releasableDraftShifts]);
  const releaseAllDepartmentOptions = useMemo(() => buildReleaseDepartmentOptions(releasableDraftShifts), [releasableDraftShifts]);
  const releasePeriodDepartmentOptions = useMemo(() => buildReleaseDepartmentOptions(periodDraftShifts), [periodDraftShifts]);
  const releaseSummary = useMemo(
    () => ({
      totalCount: releasableDraftShifts.length,
      periodCount: periodDraftShifts.length,
      periodLabel: viewMode === "week" ? "aktuelle Woche" : "aktueller Monat",
      allDepartments: releaseAllDepartmentOptions,
      periodDepartments: releasePeriodDepartmentOptions,
    }),
    [
      periodDraftShifts.length,
      releaseAllDepartmentOptions,
      releasePeriodDepartmentOptions,
      releasableDraftShifts.length,
      viewMode,
    ]
  );
  const releasePreviewShiftIds = useMemo(() => {
    if (!releasePreview?.scope) return null;

    const sourceShifts = releasePreview.scope === "period" ? periodDraftShifts : releasableDraftShifts;
    const targetShifts = filterReleaseShiftsByDepartment(sourceShifts, releasePreview.departmentKey);
    const targetIds = targetShifts.map((shift) => String(shift?.id || "")).filter(Boolean);

    return targetIds.length ? new Set(targetIds) : null;
  }, [periodDraftShifts, releasableDraftShifts, releasePreview]);

  // Beim Hovern eines Konflikts werden dessen Einträge im Raster hervorgehoben —
  // 1:1 derselbe Mechanismus wie die Freigabe-Vorschau.
  const conflictPreviewShiftIds = useMemo(() => {
    const entryIds = (Array.isArray(conflictPreview?.entryIds) ? conflictPreview.entryIds : [])
      .map((entryId) => String(entryId || ""))
      .filter(Boolean);
    return entryIds.length ? new Set(entryIds) : null;
  }, [conflictPreview]);

  /*
    Die Hervorhebung nützt nichts, wenn sie ausserhalb des Ausschnitts liegt —
    darum sagen wir dem Raster auch, welche Zelle es dafür ins Bild holen soll.
    Der erste Eintrag eines Konflikts ist immer die auslösende Schicht.
  */
  const conflictFocusCell = useMemo(() => {
    const dateKey = String(conflictPreview?.dateKey || "");
    if (!dateKey) return null;

    const shiftId = String(conflictPreview?.entryIds?.[0] || "");
    const conflictShift = shiftId
      ? (Array.isArray(shifts) ? shifts : []).find((shift) => String(shift?.id || "") === shiftId)
      : null;
    // In der Abteilungsansicht steht der Eintrag in der Zeile seiner Abteilung,
    // nicht in der des Mitarbeitenden. Ohne Schicht (z. B. weil die Suche sie
    // ausfiltert) hilft nur die Mitarbeitendenzeile.
    const rowId = conflictShift
      ? getShiftCellRowId(conflictShift, rowMode)
      : rowMode === "employee"
        ? String(conflictPreview?.employeeId || "")
        : "";

    return rowId ? { rowId, dateKey } : null;
  }, [conflictPreview, rowMode, shifts]);

  const defaultEmployeeStatsId = useMemo(() => {
    const visibleEmployee = rowMode === "employee" ? rows.find((row) => row?.id) : null;
    return String(visibleEmployee?.id || employeeOrderRows?.[0]?.id || "");
  }, [employeeOrderRows, rowMode, rows]);

  const clearFeedbackToast = useCallback(() => {
    if (feedbackToastKeyRef.current == null) return;
    closeToast(feedbackToastKeyRef.current);
    feedbackToastKeyRef.current = null;
  }, [closeToast]);

  const showFeedback = useCallback(
    (message, type = "success", options = {}) => {
      if (!message) return;

      clearFeedbackToast();
      const toastOptions = options.duration
        ? { autoHideDuration: Number(options.duration) }
        : {};
      const toastMethod =
        type === "loading"
          ? showLoadingToast
          : type === "error"
            ? showErrorToast
            : type === "warning"
              ? showWarningToast
              : type === "info"
                ? showInfoToast
                : showSuccessToast;

      feedbackToastKeyRef.current = toastMethod(message, toastOptions);
    },
    [
      clearFeedbackToast,
      showErrorToast,
      showInfoToast,
      showLoadingToast,
      showSuccessToast,
      showWarningToast,
    ]
  );

  useEffect(() => clearFeedbackToast, [clearFeedbackToast]);

  const openSettingsPanel = useCallback((section = "order") => {
    setSettingsPanel({ open: true, section });
  }, []);

  const closeSettingsPanel = useCallback(() => {
    setSettingsPanel((current) => ({ ...current, open: false }));
  }, []);

  const openPeriodCopyPanel = useCallback(() => {
    setIsPeriodCopyOpen(true);
  }, []);

  const closePeriodCopyPanel = useCallback(() => {
    setIsPeriodCopyOpen(false);
  }, []);

  const openSolverPanel = useCallback(() => {
    if (!SOLVER_PANEL_ENABLED) {
      showFeedback(SOLVER_PREVIEW_MESSAGE, "info", { duration: 4600 });
      return;
    }
    setIsSolverOpen(true);
  }, [showFeedback]);

  const closeSolverPanel = useCallback(() => {
    setIsSolverOpen(false);
  }, []);

  // Solver-Läufe: geteilter Zustand von Toolbar (Ring + Badge) und Dialog.
  // Pollt im Hintergrund, auch wenn der Dialog zu ist (Grill-Entscheid 2026-08-06).
  // Solange der Dialog zubleibt, ruht das Pollen: Ring und Zähler führen zu
  // einer Ansicht, die sich gar nicht öffnen lässt.
  const canUseSolver = ["Super_Admin", "Admin", "company_admin"].includes(accessLevel);
  const solverRuns = useSolverRuns({
    enabled: canUseSolver && SOLVER_PANEL_ENABLED,
    fast: isSolverOpen,
  });

  const toggleConflictsPanel = useCallback(() => {
    setIsConflictsPanelOpen((current) => {
      if (current) setConflictPreview(null);
      return !current;
    });
  }, []);

  const closeConflictsPanel = useCallback(() => {
    setIsConflictsPanelOpen(false);
    setConflictPreview(null);
  }, []);

  const openExportDialog = useCallback((format) => {
    setExportDialog({ open: true, format: normalizeExportFormat(format) });
  }, []);

  const closeExportDialog = useCallback(() => {
    setExportDialog((current) => ({ ...current, open: false }));
  }, []);

  const openEmployeeStatsPanel = useCallback(
    (employeeOrId = null) => {
      const directEmployeeId =
        employeeOrId && typeof employeeOrId === "object" ? employeeOrId.id : employeeOrId;
      const selectedCellEmployeeId = selectedCells.find((cell) => cell?.rowMode === "employee")?.row?.id;
      const employeeId = String(
        directEmployeeId || selectedShift?.employeeId || selectedCellEmployeeId || defaultEmployeeStatsId || ""
      );

      setEmployeeStatsPanel({ open: true, employeeId });
    },
    [defaultEmployeeStatsId, selectedCells, selectedShift?.employeeId]
  );

  const closeEmployeeStatsPanel = useCallback(() => {
    setEmployeeStatsPanel((current) => ({ ...current, open: false }));
  }, []);

  const selectEmployeeStats = useCallback((employeeId) => {
    setEmployeeStatsPanel((current) => ({ ...current, employeeId: String(employeeId || "") }));
  }, []);

  const openEditor = useCallback((context) => {
    setEditorContext(context);
  }, []);

  const closeEditor = useCallback(() => {
    setEditorContext(null);
  }, []);

  const handleEditorSubmit = useCallback(
    async ({ context, draft, releaseAfterSave = false }) => {
      if (isSavingAction) return;

      const event = buildSaveEventFromEditorDraft({ context, draft });
      if (!event) {
        showFeedback("Der Eintrag ist noch nicht vollständig.", "warning", { duration: 3200 });
        return;
      }

      const shouldReleaseAfterSave =
        Boolean(releaseAfterSave) && event.entry_type !== "absence" && event.entry_type !== "user_holiday";

      setIsSavingAction(true);
      // Speichern und Freigeben sind zwei Anfragen, fuer die Bedienung aber ein
      // Vorgang: eine einzige Laufmeldung, die beides nennt.
      const isEdit = context?.action === "edit";
      showFeedback(
        shouldReleaseAfterSave
          ? (isEdit ? "Schicht wird gespeichert und freigegeben..." : "Schicht wird erstellt und freigegeben...")
          : (isEdit ? "Eintrag wird gespeichert..." : "Eintrag wird erstellt..."),
        "loading",
        { persist: true }
      );
      setEditorContext(null);
      setSelectedShift(null);
      setSelectedShiftGroup([]);
      setSelectedUserHoliday(null);
      setSelectedCells([]);

      try {
        const savedData = await onSaveSchedulerEvents?.([event]);

        if (shouldReleaseAfterSave) {
          const savedShift = findSavedShiftForEvent(savedData, event);
          const releaseEvents = buildReleaseEventsFromShifts(savedShift ? [savedShift] : []);

          if (!releaseEvents.length) {
            showFeedback("Gespeichert, aber nicht freigegeben", "warning", { duration: 4200 });
            return;
          }

          await onSaveSchedulerEvents?.(releaseEvents);
          showFeedback(isEdit ? "Schicht gespeichert und freigegeben" : "Schicht erstellt und freigegeben");
          return;
        }

        showFeedback(isEdit ? "Eintrag gespeichert" : "Eintrag erstellt");
      } catch (saveError) {
        showFeedback(normalizeApiError(saveError).message, "error", { duration: 4200 });
      } finally {
        setIsSavingAction(false);
      }
    },
    [isSavingAction, onSaveSchedulerEvents, showFeedback]
  );

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const selectCell = useCallback((context, options = {}) => {
    setSelectedShift(null);
    setSelectedShiftGroup([]);
    setSelectedUserHoliday(null);

    if (!context) {
      setSelectedCells([]);
      return;
    }

    if (options.mode === "toggle") {
      setSelectedCells((currentCells) => toggleCellContext(currentCells, context));
      return;
    }

    if (options.mode === "add") {
      setSelectedCells((currentCells) => mergeCellSelections(currentCells, [context]));
      return;
    }

    setSelectedCells(normalizeCellSelection([context]));
  }, []);

  const selectCellRange = useCallback((contexts, options = {}) => {
    setSelectedShift(null);
    setSelectedShiftGroup([]);
    setSelectedUserHoliday(null);

    const nextContexts = normalizeCellSelection(contexts);
    if (options.mode === "add") {
      setSelectedCells((currentCells) => mergeCellSelections(currentCells, nextContexts));
      return;
    }

    setSelectedCells(nextContexts);
  }, []);

  const selectShift = useCallback((context, event) => {
    const shift = context?.shift || null;
    if (!shift) {
      setSelectedShift(null);
      setSelectedShiftGroup([]);
      setSelectedUserHoliday(null);
      setSelectedCells([]);
      return;
    }

    setSelectedUserHoliday(null);
    setSelectedCells([]);

    if (!isMultiSelectEvent(event)) {
      setSelectedShift(shift);
      setSelectedShiftGroup([shift]);
      return;
    }

    const nextGroup = toggleEntryInGroup(selectedShiftGroup, shift);
    setSelectedShiftGroup(nextGroup);
    setSelectedShift(nextGroup[nextGroup.length - 1] || null);
  }, [selectedShiftGroup]);

  const selectUserHoliday = useCallback((context) => {
    setSelectedUserHoliday(context?.holiday || null);
    setSelectedShift(null);
    setSelectedShiftGroup([]);
    setSelectedCells([]);
  }, []);

  const openCreateEditor = useCallback(
    (context) => {
      closeContextMenu();
      openEditor({ ...context, action: "create" });
    },
    [closeContextMenu, openEditor]
  );

  const openCreateAbsenceEditor = useCallback(
    (context, absenceOption = null, contexts = null) => {
      const selectedRange = buildQuickTimeOffRange(Array.isArray(contexts) && contexts.length ? contexts : null, context);
      const fallbackRange = buildQuickTimeOffRange(context);

      closeContextMenu();
      openEditor({
        ...context,
        action: "create",
        entryType: "absence",
        absenceOption,
        timeOffRange: selectedRange || fallbackRange,
      });
    },
    [closeContextMenu, openEditor]
  );

  const quickCreateTimeOff = useCallback(
    async (context, timeOffOption, contexts = null) => {
      if (isSavingAction) return;

      const drafts = buildQuickTimeOffDrafts({ context, contexts, option: timeOffOption });
      if (!drafts.length) {
        openCreateAbsenceEditor(context, timeOffOption, contexts);
        return;
      }

      const events = drafts
        .map((draft, index) => {
          const event = buildSaveEventFromEditorDraft({ context: { ...context, action: "create" }, draft });
          return event && drafts.length > 1 ? { ...event, id: `${event.id}-${index}` } : event;
        })
        .filter(Boolean);

      if (events.length !== drafts.length) {
        showFeedback("Der Eintrag ist noch nicht vollständig.", "warning", { duration: 3200 });
        return;
      }

      closeContextMenu();
      setIsSavingAction(true);
      showFeedback(events.length > 1 ? `${events.length} Einträge werden erstellt...` : "Eintrag wird erstellt...", "loading", {
        persist: true,
      });
      setEditorContext(null);
      setSelectedShift(null);
      setSelectedShiftGroup([]);
      setSelectedUserHoliday(null);
      setSelectedCells([]);

      try {
        await onSaveSchedulerEvents?.(events);
        showFeedback(events.length > 1 ? `${events.length} Einträge erstellt` : "Eintrag erstellt");
      } catch (saveError) {
        showFeedback(normalizeApiError(saveError).message, "error", { duration: 4200 });
      } finally {
        setIsSavingAction(false);
      }
    },
    [closeContextMenu, isSavingAction, onSaveSchedulerEvents, openCreateAbsenceEditor, showFeedback]
  );

  const openEditEditor = useCallback(
    (context) => {
      closeContextMenu();
      if (context?.shift) {
        setSelectedShift(context.shift);
        setSelectedShiftGroup([context.shift]);
        setSelectedUserHoliday(null);
        setSelectedCells([]);
      }
      openEditor({ ...context, action: "edit" });
    },
    [closeContextMenu, openEditor]
  );

  const openEditUserHoliday = useCallback(
    (context) => {
      if (!context?.holiday) return;
      closeContextMenu();
      setSelectedUserHoliday(context.holiday);
      setSelectedShift(null);
      setSelectedShiftGroup([]);
      setSelectedCells([]);
      openEditor({
        row: context.row,
        day: context.day,
        rowMode: context.rowMode,
        userHoliday: context.holiday,
        action: "edit",
      });
    },
    [closeContextMenu, openEditor]
  );

  const handleCellContextMenu = useCallback((event, context) => {
    event.preventDefault();
    event.stopPropagation();
    const position = getContextMenuPosition(event, 3, 420);
    const contextKey = getCellKey(context);
    const useCurrentSelection = contextKey && selectedCellKeys.has(contextKey) && selectedCells.length > 1;
    const contexts = useCurrentSelection ? selectedCells : [context];
    if (!useCurrentSelection) {
      selectCell(context);
    }
    setContextMenu({
      type: "cell",
      x: position.x,
      y: position.y,
      context,
      contexts,
    });
  }, [selectCell, selectedCellKeys, selectedCells]);

  const handleShiftContextMenu = useCallback((event, context) => {
    event.preventDefault();
    event.stopPropagation();
    const position = getContextMenuPosition(event, 3);
    const shift = context?.shift || null;
    const shiftKey = getEntryKey(shift);
    const useCurrentSelection = shiftKey && selectedShiftIds.has(shiftKey) && selectedShiftGroup.length > 1;
    const entries = useCurrentSelection ? selectedShiftGroup : normalizeEntryGroup(shift);
    if (!useCurrentSelection) {
      selectShift(context);
    }
    setContextMenu({
      type: "shift",
      x: position.x,
      y: position.y,
      shift,
      entries,
      context,
    });
  }, [selectShift, selectedShiftGroup, selectedShiftIds]);

  const handleUserHolidayContextMenu = useCallback((event, context) => {
    event.preventDefault();
    event.stopPropagation();
    const position = getContextMenuPosition(event, 3);
    selectUserHoliday(context);
    setContextMenu({
      type: "user_holiday",
      x: position.x,
      y: position.y,
      holiday: context?.holiday || null,
      context,
    });
  }, [selectUserHoliday]);

  const copyEntry = useCallback((entry = selectedShiftGroup.length > 1 ? selectedShiftGroup : selectedShift) => {
    const entries = normalizeEntryGroup(entry);
    if (!entries.length) return;

    setCopiedEntry(entries.length === 1 ? entries[0] : entries);
    showFeedback(`${getEntryGroupLabel(entries)} kopiert`);
    closeContextMenu();
  }, [closeContextMenu, selectedShift, selectedShiftGroup, showFeedback]);

  const pasteCopiedEntry = useCallback(
    async (targetCells = selectedCells) => {
      if (!copiedEntry || isSavingAction) return;

      const employeeCells = (Array.isArray(targetCells) ? targetCells : []).filter(
        (cell) => cell?.rowMode === "employee"
      );
      if (!employeeCells.length) {
        showFeedback("Einfügen ist aktuell in der Mitarbeitenden-Ansicht möglich.", "warning", { duration: 3200 });
        closeContextMenu();
        return;
      }

      const copiedEntries = normalizeEntryGroup(copiedEntry);
      const copiedEntryLabel = getEntryGroupLabel(copiedEntries);
      const events = buildCreateEventsFromCopiedEntry(copiedEntry, employeeCells);
      const optimisticEntries = buildOptimisticCopiedEntries(copiedEntry, employeeCells);
      if (!events.length) {
        showFeedback("Der kopierte Eintrag konnte für diese Auswahl nicht eingefügt werden.", "warning", {
          duration: 3400,
        });
        closeContextMenu();
        return;
      }

      setOptimisticPaste(optimisticEntries);
      setSelectedShift(null);
      setSelectedShiftGroup([]);
      setSelectedUserHoliday(null);
      setSelectedCells(employeeCells);
      closeContextMenu();
      setIsSavingAction(true);
      showFeedback(
        events.length === 1 ? `${copiedEntryLabel} wird eingefügt...` : `${events.length} Einträge werden eingefügt...`,
        "loading",
        { persist: true }
      );
      try {
        await onSaveSchedulerEvents?.(events);
        showFeedback(events.length === 1 ? `${copiedEntryLabel} eingefügt` : `${events.length} Einträge eingefügt`);
      } catch (saveError) {
        showFeedback(normalizeApiError(saveError).message, "error", { duration: 4200 });
      } finally {
        setIsSavingAction(false);
        setOptimisticPaste(null);
      }
    },
    [closeContextMenu, copiedEntry, isSavingAction, onSaveSchedulerEvents, selectedCells, showFeedback]
  );

  const copyPeriod = useCallback(
    async (preview) => {
      const events = Array.isArray(preview?.events) ? preview.events : [];
      if (!events.length || isSavingAction) return;

      setIsSavingAction(true);
      showFeedback(events.length > 1 ? `${events.length} Einträge werden kopiert...` : "Eintrag wird kopiert...", "loading", {
        persist: true,
      });
      try {
        await onSaveSchedulerEvents?.(events);
        showFeedback(events.length > 1 ? `${events.length} Einträge kopiert` : "Eintrag kopiert");
      } catch (saveError) {
        showFeedback(normalizeApiError(saveError).message, "error", { duration: 4200 });
        throw saveError;
      } finally {
        setIsSavingAction(false);
      }
    },
    [isSavingAction, onSaveSchedulerEvents, showFeedback]
  );

  const deleteEntry = useCallback(
    async (entry = selectedShiftGroup.length > 1 ? selectedShiftGroup : selectedShift || selectedUserHoliday) => {
      if (!entry || isSavingAction) return;

      const entries = normalizeEntryGroup(entry);
      const events = entries.map(buildDeleteEventFromEntry).filter(Boolean);
      if (!events.length) {
        showFeedback("Dieser Eintrag kann aktuell nicht gelöscht werden.", "warning", { duration: 3200 });
        closeContextMenu();
        return;
      }

      const entryLabel = getEntryGroupLabel(entries);
      setIsSavingAction(true);
      showFeedback(`${entryLabel} wird gelöscht...`, "loading", { persist: true });
      try {
        await onSaveSchedulerEvents?.(events);
        setSelectedShift(null);
        setSelectedShiftGroup([]);
        setSelectedUserHoliday(null);
        closeContextMenu();
        showFeedback(`${entryLabel} gelöscht`);
      } catch (saveError) {
        showFeedback(normalizeApiError(saveError).message, "error", { duration: 4200 });
      } finally {
        setIsSavingAction(false);
      }
    },
    [
      closeContextMenu,
      isSavingAction,
      onSaveSchedulerEvents,
      selectedShift,
      selectedShiftGroup,
      selectedUserHoliday,
      showFeedback,
    ]
  );

  const deleteEditorEntry = useCallback(
    (entry) => {
      if (!entry || isSavingAction) return;
      closeEditor();
      deleteEntry(entry);
    },
    [closeEditor, deleteEntry, isSavingAction]
  );

  const moveShift = useCallback(
    async (shift, targetCell) => {
      if (!shift || !targetCell || isSavingAction) return;
      if (isShiftAlreadyInCell(shift, targetCell)) {
        setDragState(EMPTY_DRAG_STATE);
        return;
      }

      const event = buildMoveEventFromShift(shift, targetCell);
      const optimisticShift = buildOptimisticMovedShift(shift, targetCell);
      if (!event) {
        showFeedback(t("shiftplan.move_invalid"), "warning", { duration: 3200 });
        return;
      }

      if (optimisticShift) {
        setOptimisticMove({ shift: optimisticShift });
        setSelectedShift(optimisticShift);
        setSelectedShiftGroup([optimisticShift]);
      }
      setDragState(EMPTY_DRAG_STATE);
      setIsSavingAction(true);
      try {
        const saveResult = await onSaveSchedulerEvents?.([event], {
          localShiftMoves: optimisticShift ? [optimisticShift] : [],
        });
        setSelectedShift(null);
        setSelectedShiftGroup([]);
        setSelectedUserHoliday(null);
        setSelectedCells([]);
        showFeedback(
          saveResult?.demoLocal ? t("shiftplan.move_demo_success") : t("shiftplan.move_success"),
          "success",
          { duration: 3000 }
        );
      } catch (saveError) {
        setOptimisticMove(null);
        setSelectedShift(shift);
        setSelectedShiftGroup([shift]);
        showFeedback(normalizeApiError(saveError).message, "error", { duration: 4200 });
      } finally {
        setIsSavingAction(false);
        setOptimisticMove(null);
      }
    },
    [isSavingAction, onSaveSchedulerEvents, showFeedback, t]
  );

  const resizeSpanEntry = useCallback(
    async ({ entry, startDate, endDate } = {}) => {
      if (!entry || isSavingAction) return;

      const event = buildResizeEventFromEntry(entry, { startDate, endDate });
      const optimisticEntry = buildOptimisticResizedEntry(entry, { startDate, endDate });
      if (!event || !optimisticEntry) {
        showFeedback("Dieser Eintrag kann aktuell nicht angepasst werden.", "warning", { duration: 3200 });
        return;
      }

      const isUserHoliday = isUserHolidayEntry(optimisticEntry);
      const entryLabel = getCopiedEntryLabel(optimisticEntry);

      setOptimisticResize({ entry: optimisticEntry });
      if (isUserHoliday) {
        setSelectedUserHoliday(optimisticEntry);
        setSelectedShift(null);
        setSelectedShiftGroup([]);
      } else {
        setSelectedShift(optimisticEntry);
        setSelectedShiftGroup([optimisticEntry]);
        setSelectedUserHoliday(null);
      }
      setSelectedCells([]);
      closeContextMenu();
      setIsSavingAction(true);
      showFeedback(`${entryLabel} wird angepasst...`, "loading", { persist: true });

      try {
        await onSaveSchedulerEvents?.([event]);
        setSelectedShift(null);
        setSelectedShiftGroup([]);
        setSelectedUserHoliday(null);
        showFeedback(`${entryLabel} angepasst`);
      } catch (saveError) {
        setOptimisticResize(null);
        showFeedback(normalizeApiError(saveError).message, "error", { duration: 4200 });
      } finally {
        setIsSavingAction(false);
        setOptimisticResize(null);
      }
    },
    [closeContextMenu, isSavingAction, onSaveSchedulerEvents, showFeedback]
  );

  const notifyReleasedEmployees = useCallback(
    async (notifyUserIds, baseLabel) => {
      showFeedback("Mitarbeitende werden per WhatsApp benachrichtigt...", "loading", { persist: true });

      try {
        const result = await onSendReleaseNotifications?.(notifyUserIds);
        const notifiedCount = Number(result?.notified_count ?? 0);
        const skipped = Array.isArray(result?.skipped) ? result.skipped : [];

        if (skipped.length > 0) {
          // "nicht erreichbar" stimmt nur ohne Telefonnummer. Bricht der
          // Versand ab, ist die Nummer da und WhatsApp lehnt ab — das muss
          // man unterscheiden koennen, sonst sucht man am falschen Ende.
          const failedCount = skipped.filter((entry) => entry?.reason === "send_failed").length;
          const unreachableCount = skipped.length - failedCount;
          const reasons = [
            unreachableCount > 0 ? `${unreachableCount} nicht erreichbar` : "",
            failedCount > 0 ? `${failedCount} von WhatsApp abgelehnt` : "",
          ].filter(Boolean);

          showFeedback(
            `${baseLabel} · ${notifiedCount} benachrichtigt, ${reasons.join(", ")}`,
            "warning",
            { duration: 5200 }
          );
          return;
        }

        showFeedback(`${baseLabel} · ${notifiedCount} per WhatsApp benachrichtigt`);
      } catch (notifyError) {
        showFeedback(`${baseLabel} · Benachrichtigung fehlgeschlagen`, "warning", { duration: 5200 });
      }
    },
    [onSendReleaseNotifications, showFeedback]
  );

  const releaseShifts = useCallback(
    async (targetShifts, successLabel, notifyUserIds = []) => {
      if (isSavingAction) return;

      const events = buildReleaseEventsFromShifts(targetShifts);
      if (!events.length) {
        showFeedback("Keine offenen Schichten für die Freigabe gefunden.", "warning", { duration: 3200 });
        return;
      }

      setIsSavingAction(true);
      showFeedback(
        events.length === 1 ? "Schicht wird freigegeben..." : `${events.length} Schichten werden freigegeben...`,
        "loading",
        { persist: true }
      );

      try {
        await onSaveSchedulerEvents?.(events);
        setSelectedShift(null);
        setSelectedShiftGroup([]);
        setSelectedUserHoliday(null);
        closeContextMenu();

        const baseLabel =
          successLabel || (events.length === 1 ? "Schicht freigegeben" : `${events.length} Schichten freigegeben`);
        if (notifyUserIds.length) {
          await notifyReleasedEmployees(notifyUserIds, baseLabel);
        } else {
          showFeedback(baseLabel);
        }
      } catch (saveError) {
        showFeedback(normalizeApiError(saveError).message, "error", { duration: 4200 });
      } finally {
        setIsSavingAction(false);
      }
    },
    [closeContextMenu, isSavingAction, notifyReleasedEmployees, onSaveSchedulerEvents, showFeedback]
  );

  const employeesById = useMemo(
    () => new Map((Array.isArray(employeeOrderRows) ? employeeOrderRows : []).map((employee) => [String(employee.id), employee])),
    [employeeOrderRows]
  );

  const buildReleaseRecipients = useCallback(
    (scope, departmentKey) => {
      const sourceShifts = scope === "period" ? periodDraftShifts : releasableDraftShifts;
      const targetShifts = filterReleaseShiftsByDepartment(sourceShifts, departmentKey);
      const recipientsById = new Map();

      targetShifts.forEach((shift) => {
        const employeeId = String(shift?.employeeId || "");
        if (!employeeId) return;

        const current = recipientsById.get(employeeId);
        if (current) {
          current.shiftCount += 1;
          return;
        }

        const employee = employeesById.get(employeeId);
        recipientsById.set(employeeId, {
          id: employeeId,
          name: employee?.name || shift.employeeName || `Mitarbeitende ${employeeId}`,
          hasPhone: Boolean(employee?.hasPhone),
          shiftCount: 1,
        });
      });

      return Array.from(recipientsById.values()).sort((left, right) =>
        left.name.localeCompare(right.name, "de", { sensitivity: "base" })
      );
    },
    [employeesById, periodDraftShifts, releasableDraftShifts]
  );

  const handleReleaseAllDrafts = useCallback(
    (departmentKey, notifyUserIds = []) => {
      const department = releaseAllDepartmentOptions.find((item) => item.key === departmentKey);
      const targetShifts = filterReleaseShiftsByDepartment(releasableDraftShifts, departmentKey);
      releaseShifts(
        targetShifts,
        `${targetShifts.length} Schichten${department?.name ? ` für ${department.name}` : ""} freigegeben`,
        notifyUserIds
      );
    },
    [releasableDraftShifts, releaseAllDepartmentOptions, releaseShifts]
  );

  const handleReleasePeriodDrafts = useCallback(
    (departmentKey, notifyUserIds = []) => {
      const department = releasePeriodDepartmentOptions.find((item) => item.key === departmentKey);
      const targetShifts = filterReleaseShiftsByDepartment(periodDraftShifts, departmentKey);
      releaseShifts(
        targetShifts,
        `${targetShifts.length} Schichten${department?.name ? ` für ${department.name}` : ""} im Zeitraum freigegeben`,
        notifyUserIds
      );
    },
    [periodDraftShifts, releasePeriodDepartmentOptions, releaseShifts]
  );

  const createShiftFromDemandDrop = useCallback(
    async (demandEntry, targetCell) => {
      setDragState(EMPTY_DRAG_STATE);
      if (isSavingAction) return;

      if (targetCell?.rowMode !== "employee") {
        showFeedback("Bedarf kann nur in der Mitarbeitenden-Ansicht geplant werden.", "warning", { duration: 3200 });
        return;
      }

      const event = buildCreateEventFromDemandDrop(demandEntry, targetCell);
      if (!event) {
        showFeedback(
          `Dieser Bedarf gilt für ${formatDemandDayLabel(demandEntry?.dateKey)} und kann nur dort geplant werden.`,
          "warning",
          { duration: 3400 }
        );
        return;
      }

      const employeeName = String(targetCell?.row?.name || "").trim();
      setSelectedCells([]);
      setIsSavingAction(true);
      showFeedback("Schicht wird erstellt...", "loading", { persist: true });
      try {
        await onSaveSchedulerEvents?.([event]);
        showFeedback(
          employeeName
            ? `${demandEntry.shiftName || "Schicht"} für ${employeeName} eingeplant`
            : `${demandEntry.shiftName || "Schicht"} eingeplant`
        );
      } catch (saveError) {
        showFeedback(normalizeApiError(saveError).message, "error", { duration: 4200 });
      } finally {
        setIsSavingAction(false);
      }
    },
    [isSavingAction, onSaveSchedulerEvents, showFeedback]
  );

  const moveDraggedEntry = useCallback(
    (entry, targetCell) => {
      if (!entry || !targetCell) return;

      if (isDemandDragEntry(entry)) {
        createShiftFromDemandDrop(entry, targetCell);
        return;
      }

      const targetStartCell = getDragStartCellContext(targetCell, days, dragState.anchorDayOffset);
      const movedRange = buildMovedCalendarEntryRange(entry, targetStartCell);

      if (isUserHolidayEntry(entry)) {
        if (!movedRange) {
          setDragState(EMPTY_DRAG_STATE);
          return;
        }

        if (isCalendarEntryAlreadyAtTarget(entry, targetStartCell, movedRange)) {
          setDragState(EMPTY_DRAG_STATE);
          return;
        }

        const movedHoliday = {
          ...entry,
          userId: String(targetStartCell?.row?.id || entry.userId || ""),
          employeeId: String(targetStartCell?.row?.id || entry.employeeId || ""),
        };
        setDragState(EMPTY_DRAG_STATE);
        resizeSpanEntry({ entry: movedHoliday, ...movedRange });
        return;
      }

      moveShift(entry, targetStartCell);
    },
    [createShiftFromDemandDrop, days, dragState.anchorDayOffset, moveShift, resizeSpanEntry]
  );

  const handleEntryDragStart = useCallback((context) => {
    const entry = context?.entry || context?.shift || context?.holiday;
    if (!entry) return;

    closeContextMenu();
    if (isUserHolidayEntry(entry)) {
      setSelectedUserHoliday(entry);
      setSelectedShift(null);
      setSelectedShiftGroup([]);
    } else {
      setSelectedShift(entry);
      setSelectedShiftGroup([entry]);
      setSelectedUserHoliday(null);
    }
    setSelectedCells([]);
    setDragState({
      entry,
      targetCellKey: "",
      anchorDayOffset: Math.max(0, Math.floor(Number(context.dragDayOffset) || 0)),
    });
  }, [closeContextMenu]);

  const handleEntryDragEnd = useCallback(() => {
    setDragState(EMPTY_DRAG_STATE);
  }, []);

  const handleDemandDragStart = useCallback(
    (demandDragEntry) => {
      if (!demandDragEntry || isSavingAction) return;

      closeContextMenu();
      setSelectedShift(null);
      setSelectedShiftGroup([]);
      setSelectedUserHoliday(null);
      setSelectedCells([]);
      setDragState({ entry: demandDragEntry, targetCellKey: "", anchorDayOffset: 0 });
    },
    [closeContextMenu, isSavingAction]
  );

  const handleDemandToggleExpanded = useCallback((nextExpanded) => {
    setDemandExpanded(Boolean(nextExpanded));
    persistStoredFlag(DEMAND_EXPANDED_STORAGE_KEY, Boolean(nextExpanded));
  }, []);

  const handleShowDemandChange = useCallback((nextVisible) => {
    setShowDemand(Boolean(nextVisible));
    persistStoredFlag(DEMAND_VISIBLE_STORAGE_KEY, Boolean(nextVisible));
  }, []);

  const handleShiftDropOnDemand = useCallback(() => {
    const draggedShift = dragState.entry;
    setDragState(EMPTY_DRAG_STATE);
    if (!draggedShift || draggedShift.entryType !== "shift" || isDemandDragEntry(draggedShift)) return;

    deleteEntry(draggedShift);
  }, [deleteEntry, dragState.entry]);

  const handleCellDragTarget = useCallback((context) => {
    if (!dragState.entry || !context) return;
    setDragState((current) => {
      const targetCellKey = getCellKey(context);
      if (!current.entry || current.targetCellKey === targetCellKey) return current;
      return { ...current, targetCellKey };
    });
  }, [dragState.entry]);

  const handleCellDropEntry = useCallback(
    (context) => {
      if (!dragState.entry || !context) return;
      moveDraggedEntry(dragState.entry, context);
    },
    [dragState.entry, moveDraggedEntry]
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (editorContext || settingsPanel.open || employeeStatsPanel.open || isPeriodCopyOpen || isTypingTarget(event.target)) return;

      const key = String(event.key || "").toLowerCase();
      const isModifier = event.ctrlKey || event.metaKey;

      const selectedShiftEntries = selectedShiftGroup.length ? selectedShiftGroup : normalizeEntryGroup(selectedShift);

      if (isModifier && key === "c" && selectedShiftEntries.length) {
        event.preventDefault();
        copyEntry(selectedShiftEntries);
        return;
      }

      if (isModifier && key === "c" && selectedUserHoliday) {
        event.preventDefault();
        copyEntry(selectedUserHoliday);
        return;
      }

      if (isModifier && key === "v" && copiedEntry && selectedCells.length) {
        event.preventDefault();
        pasteCopiedEntry(selectedCells);
        return;
      }

      const selectedEntry = selectedShiftEntries.length ? selectedShiftEntries : selectedUserHoliday;
      if ((event.key === "Delete" || event.key === "Backspace") && normalizeEntryGroup(selectedEntry).length) {
        event.preventDefault();
        deleteEntry(selectedEntry);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    copiedEntry,
    copyEntry,
    deleteEntry,
    employeeStatsPanel.open,
    editorContext,
    isPeriodCopyOpen,
    pasteCopiedEntry,
    selectedCells,
    selectedShift,
    selectedShiftGroup,
    selectedUserHoliday,
    settingsPanel.open,
  ]);

  useEffect(() => {
    if (!contextMenu) return undefined;

    // Scrollen INNERHALB des Menüs (lange Absenz-Liste im Untermenü) darf es
    // nicht schliessen — nur Scrollen der Seite bzw. des Rasters dahinter.
    const closeOnOutsideScroll = (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest(".spv2-grid-context-menu")) return;
      closeContextMenu();
    };

    window.addEventListener("click", closeContextMenu);
    window.addEventListener("resize", closeContextMenu);
    window.addEventListener("scroll", closeOnOutsideScroll, true);

    return () => {
      window.removeEventListener("click", closeContextMenu);
      window.removeEventListener("resize", closeContextMenu);
      window.removeEventListener("scroll", closeOnOutsideScroll, true);
    };
  }, [closeContextMenu, contextMenu]);

  useEffect(() => {
    const clearSelectionOutsideGrid = (event) => {
      const target = event.target;
      if (target?.closest?.(".spv2-grid-shell") || target?.closest?.(".spv2-grid-context-menu")) return;
      setSelectedShift(null);
      setSelectedShiftGroup([]);
      setSelectedUserHoliday(null);
      setSelectedCells([]);
    };

    window.addEventListener("mousedown", clearSelectionOutsideGrid);
    return () => window.removeEventListener("mousedown", clearSelectionOutsideGrid);
  }, []);

  useEffect(() => {
    setSelectedShift(null);
    setSelectedShiftGroup([]);
    setSelectedUserHoliday(null);
    setSelectedCells([]);
    setContextMenu(null);
    setDragState(EMPTY_DRAG_STATE);
    setReleasePreview(null);
    setOptimisticResize(null);
  }, [referenceDate, rowMode, viewMode]);

  const copiedEntries = useMemo(() => normalizeEntryGroup(copiedEntry), [copiedEntry]);
  const copiedUserHolidayId = copiedEntries.length === 1 && isUserHolidayEntry(copiedEntries[0]) ? copiedEntries[0]?.id || "" : "";
  const copiedTimetableEntryIds = useMemo(
    () =>
      new Set(
        copiedEntries.filter((entry) => !isUserHolidayEntry(entry)).map((entry) => getEntryKey(entry)).filter(Boolean)
      ),
    [copiedEntries]
  );
  const copiedTimetableEntryId =
    copiedTimetableEntryIds.size === 1 ? Array.from(copiedTimetableEntryIds.values())[0] : "";

  return (
    <Box className="spv2-root spv2-page-shell">
      <ShiftplanV2Toolbar
        viewMode={viewMode}
        rowMode={rowMode}
        referenceDate={referenceDate}
        periodLabel={periodLabel}
        searchQuery={searchQuery}
        onViewModeChange={onViewModeChange}
        onRowModeChange={onRowModeChange}
        onSearchQueryChange={onSearchQueryChange}
        onPreviousPeriod={onPreviousPeriod}
        onNextPeriod={onNextPeriod}
        onPeriodSelect={onPeriodSelect}
        onOpenSettings={openSettingsPanel}
        onOpenPeriodCopy={openPeriodCopyPanel}
        onOpenExport={openExportDialog}
        onOpenSolver={openSolverPanel}
        canUseSolver={canUseSolver}
        isSolverOpen={isSolverOpen}
        isSolverRunning={solverRuns.hasActiveRun}
        solverBadgeCount={solverRuns.pendingResultCount}
        isSettingsOpen={settingsPanel.open}
        isPeriodCopyOpen={isPeriodCopyOpen}
        isExportOpen={exportDialog.open}
        releaseSummary={releaseSummary}
        isReleaseSaving={isSavingAction}
        canNotifyOnRelease={Boolean(whatsappSubscription)}
        buildReleaseRecipients={buildReleaseRecipients}
        onReleaseAllDrafts={handleReleaseAllDrafts}
        onReleasePeriodDrafts={handleReleasePeriodDrafts}
        onReleasePreviewChange={setReleasePreview}
        conflictModel={conflictModel}
        isConflictsOpen={isConflictsPanelOpen}
        onToggleConflicts={toggleConflictsPanel}
        showAvailability={showAvailability}
        onShowAvailabilityChange={setShowAvailability}
      />

      {error ? (
        <Alert severity="error" className="spv2-alert">
          {error}
        </Alert>
      ) : null}

      {isLoading && !shifts.length && !rows.length ? (
        // Ladezustand nimmt die Fläche des Rasters ein, damit beim Erscheinen
        // der Daten nichts springt. Spinner kommt aus dem DS.
        <Box className="spv2-content-loading">
          <AppLoadingOverlay open scope="contained" blocking={false} label="Laden…" />
        </Box>
      ) : (
        <>
          {/* Das Konflikte-Panel dockt rechts an und schiebt das Raster zusammen. */}
          <Box className="spv2-grid-with-side-panel">
            <ShiftplanMonthGrid
              viewMode={viewMode}
              rowMode={rowMode}
              days={days}
              rows={rows}
              shiftsByCell={renderedShiftsByCell}
              availabilityByCell={availabilityByCell}
              userHolidaysByCell={renderedUserHolidaysByCell}
              availabilityAbsencesByCell={availabilityAbsencesByCell}
              showAvailability={showAvailability && rowMode === "employee"}
              workloadByEmployeeId={workloadByEmployeeId}
              publicHolidayNamesByDate={publicHolidayNamesByDate}
              dayCommentsByDate={dayCommentsByDate}
              onSaveDayComment={onSaveDayComment}
              selectedShiftId={selectedShift?.id || ""}
              selectedShiftIds={selectedShiftIds}
              selectedUserHolidayId={selectedUserHoliday?.id || ""}
              selectedCellKeys={selectedCellKeys}
              copiedTimetableEntryId={copiedTimetableEntryId}
              copiedTimetableEntryIds={copiedTimetableEntryIds}
              copiedUserHolidayId={copiedUserHolidayId}
              draggedEntry={dragState.entry}
              dragTargetCellKey={dragState.targetCellKey}
              dragAnchorDayOffset={dragState.anchorDayOffset}
              releasePreviewShiftIds={releasePreviewShiftIds}
              conflictPreviewShiftIds={conflictPreviewShiftIds}
              focusCell={conflictFocusCell}
              shiftDisplayMode={shiftDisplayMode}
              demandAvailable={demandModel.hasDemand}
              demandVisible={showDemand}
              demandByDay={demandModel.byDay}
              demandExpanded={demandExpanded}
              demandCompact={viewMode === "month"}
              demandTotalMissing={demandModel.totalMissing}
              demandTotalOver={demandModel.totalOver}
              canDragDemand={rowMode === "employee" && !isSavingAction}
              onDemandVisibleChange={handleShowDemandChange}
              onDemandToggleExpanded={handleDemandToggleExpanded}
              onDemandDragStart={handleDemandDragStart}
              onDemandDragEnd={handleEntryDragEnd}
              onShiftDropOnDemand={handleShiftDropOnDemand}
              onCellSelect={selectCell}
              onCellRangeSelect={selectCellRange}
              onCellDoubleClick={openCreateEditor}
              onCellContextMenu={handleCellContextMenu}
              onCellDragTarget={handleCellDragTarget}
              onCellDropEntry={handleCellDropEntry}
              onShiftSelect={selectShift}
              onShiftDoubleClick={openEditEditor}
              onShiftContextMenu={handleShiftContextMenu}
              onEntryDragStart={handleEntryDragStart}
              onEntryDragEnd={handleEntryDragEnd}
              onEmployeeStatsOpen={openEmployeeStatsPanel}
              onUserHolidaySelect={selectUserHoliday}
              onUserHolidayDoubleClick={openEditUserHoliday}
              onUserHolidayContextMenu={handleUserHolidayContextMenu}
              onSpanResizeCommit={isSavingAction ? undefined : resizeSpanEntry}
            />
            <ConflictsPanel
              open={isConflictsPanelOpen}
              conflictModel={conflictModel}
              periodLabel={periodLabel}
              onConflictHover={setConflictPreview}
              onClose={closeConflictsPanel}
            />
          </Box>
          <PeriodSummaryBar
            summary={periodSummary}
            scopeLabel={viewMode === "week" ? "Aktuelle Woche" : "Aktueller Monat"}
          />
        </>
      )}

      <GridContextMenu
        menu={contextMenu}
        absenceOptions={absenceOptions}
        userHolidayTypes={userHolidayTypes}
        hasCopiedEntry={Boolean(copiedEntry)}
        isSaving={isSavingAction}
        onCopy={copyEntry}
        onPaste={pasteCopiedEntry}
        onEdit={openEditEditor}
        onEditUserHoliday={openEditUserHoliday}
        onCreate={openCreateEditor}
        onCreateAbsence={openCreateAbsenceEditor}
        onQuickCreateTimeOff={quickCreateTimeOff}
        onDelete={deleteEntry}
      />

      <ShiftEditorPopup
        open={Boolean(editorContext)}
        context={editorContext}
        employees={employeeOrderRows}
        departments={departments}
        shiftTemplates={shiftTemplates}
        absenceOptions={absenceOptions}
        userHolidayTypes={userHolidayTypes}
        availabilityEntries={availabilityEntries}
        availabilityDefaults={availabilityDefaults}
        companyAvailabilityMode={companyAvailabilityMode}
        userAvailabilityModes={userAvailabilityModes}
        workingDaysPerWeek={workingDaysPerWeek}
        onClose={closeEditor}
        onSubmit={handleEditorSubmit}
        onDelete={deleteEditorEntry}
      />

      <SettingsPanel
        open={settingsPanel.open}
        activeSection={settingsPanel.section}
        initialOrderMode={rowMode}
        locationName={locationName}
        employees={employeeOrderRows}
        departments={departmentOrderRows}
        colorDepartments={departments}
        shiftTemplates={shiftTemplates}
        referenceDate={referenceDate}
        displayCalendar={displayCalendar}
        shiftDisplayMode={shiftDisplayMode}
        onClose={closeSettingsPanel}
        onFeedback={showFeedback}
        onSaveEmployeeOrder={onSaveEmployeeOrder}
        onResetEmployeeOrder={onResetEmployeeOrder}
        onSaveDepartmentOrder={onSaveDepartmentOrder}
        onResetDepartmentOrder={onResetDepartmentOrder}
        onPublicHolidaysChanged={onPublicHolidaysChanged}
        onDisplayCalendarChange={onDisplayCalendarChange}
        onDepartmentColorChange={onDepartmentColorChange}
        onShiftColorChange={onShiftColorChange}
        onShiftDisplayModeChange={onShiftDisplayModeChange}
      />

      <SolverPanel
        open={isSolverOpen}
        referenceDate={referenceDate}
        locationName={locationName}
        accessLevel={accessLevel}
        shiftDisplayMode={shiftDisplayMode}
        runs={solverRuns.runs}
        availableLocations={solverRuns.availableLocations}
        currentLocationId={solverRuns.currentLocationId}
        workerAlive={solverRuns.workerAlive}
        onRefreshRuns={solverRuns.refresh}
        onClose={closeSolverPanel}
        onTransferred={onSolverTransferred}
      />

      <PeriodCopyPanel
        open={isPeriodCopyOpen}
        viewMode={viewMode}
        referenceDate={referenceDate}
        shifts={allShifts || shifts}
        userHolidays={userHolidays}
        onLoadPlanRange={onLoadPlanRange}
        employees={employeeOrderRows}
        departments={departments}
        shiftTemplates={shiftTemplates}
        locationName={locationName}
        isSaving={isSavingAction}
        onClose={closePeriodCopyPanel}
        onCopy={copyPeriod}
      />

      <EmployeeStatsPanel
        open={employeeStatsPanel.open}
        employees={employeeOrderRows}
        shifts={allShifts || shifts}
        onLoadPlanRange={onLoadPlanRange}
        referenceDate={referenceDate}
        selectedEmployeeId={employeeStatsPanel.employeeId || defaultEmployeeStatsId}
        workingDaysPerWeek={workingDaysPerWeek}
        absenceOptions={absenceOptions}
        vacationAbsenceIds={vacationAbsenceIds}
        userHolidays={userHolidays}
        onEmployeeSelect={selectEmployeeStats}
        onClose={closeEmployeeStatsPanel}
      />

      <ShiftplanExportDialog
        open={exportDialog.open}
        initialFormat={exportDialog.format}
        referenceDate={referenceDate}
        employees={employeeOrderRows}
        shifts={allShifts || shifts}
        userHolidays={userHolidays}
        publicHolidayNamesByDate={publicHolidayNamesByDate}
        onLoadPlanRange={onLoadPlanRange}
        companyName={companyName}
        wiesnshopExcelExport={wiesnshopExcelExport}
        onClose={closeExportDialog}
      />
    </Box>
  );
}
